import { Box, Grid2 as Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DATE_FIELDS, REPORTED_CONSIDERATION_COMMENT_FIELD, SUMMARY_FIELDS } from '../constants';
import { styles } from '../styles';
import type { DealDetailsFormField, DealDetailsFormValue, DealDetailsFormValues } from '../types';
import { FormSection } from './FormSection';

type SummarySectionProps = {
  values: DealDetailsFormValues;
  onChange: (field: DealDetailsFormField, value: DealDetailsFormValue) => void;
  errors?: Record<string, boolean>;
};

const SummarySectionWithProviders = ({ values, onChange, errors = {} }: SummarySectionProps) => (
  <FormSection title='Summary'>
    {/* Fields */}
    <Grid container spacing={2}>
      {DATE_FIELDS.map(field => (
        <Grid size={{ sm: 12, md: 4 }} key={field.key}>
          <Box sx={{ mb: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
              {field.label}
            </Typography>
          </Box>
          <DatePicker
            sx={styles.textField}
            format='DD MMM YYYY'
            value={values[field.key] ? dayjs(values[field.key]) : null}
            onChange={newValue => onChange(field, newValue)}
          />
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={2} sx={{ mt: 2 }}>
      {SUMMARY_FIELDS.map(field => (
        <Grid size={{ sm: 12, md: 4 }} key={field.key}>
          <Box sx={{ mb: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
              {field.label}
            </Typography>
          </Box>
          {field.type === 'select' ? (
            <Select
              sx={styles.select}
              fullWidth
              onChange={event => onChange(field, event.target.value)}
              value={values[field.key] || ''}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              sx={styles.textField}
              name={field.key}
              value={values[field.key] || ''}
              error={errors[field.key] || false}
              helperText={errors[field.key] && field.type === 'number' ? 'Must be a number' : ''}
              onChange={event => onChange(field, event.target.value)}
              fullWidth
            />
          )}
        </Grid>
      ))}
    </Grid>

    {/* Special case for Consideration Comment */}
    <Box sx={{ mt: 2 }}>
      <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
        Consideration Comment
      </Typography>
    </Box>
    <TextField
      sx={styles.textField}
      name={REPORTED_CONSIDERATION_COMMENT_FIELD.key}
      value={values.reportedConsiderationComment}
      onChange={event => onChange(REPORTED_CONSIDERATION_COMMENT_FIELD, event.target.value)}
      fullWidth
      multiline
      minRows={3}
    />
  </FormSection>
);

export const SummarySection = ({ values, onChange, errors = {} }: SummarySectionProps) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <SummarySectionWithProviders values={values} onChange={onChange} errors={errors} />
  </LocalizationProvider>
);
